const BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/** Origin of the API, for links the browser must navigate to (the OAuth flow). */
export const apiBase = BASE;

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`api ${status}`);
  }
}

/**
 * Fires when the API rejects a request as unauthenticated, so the shell can
 * drop back to the sign-in screen instead of every page rendering its own
 * error. A session lasts a week, so this mostly means "it finally expired".
 */
const SESSION_LOST_EVENT = "health:session-lost";

export function onSessionLost(handler: () => void): () => void {
  window.addEventListener(SESSION_LOST_EVENT, handler);
  return () => window.removeEventListener(SESSION_LOST_EVENT, handler);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const body = await res.json().catch(() => null);
  if (res.status === 401) {
    window.dispatchEvent(new Event(SESSION_LOST_EVENT));
  }
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export interface SessionUser {
  sub: string;
  email: string;
  name?: string;
}

/** Throws when nobody is signed in; the 403 case means the wrong account. */
export async function getSession(): Promise<{ user: SessionUser }> {
  const res = await fetch(`${BASE}/auth/me`, { credentials: "include" });
  if (res.status === 403) throw new Error("forbidden");
  if (!res.ok) throw new Error("unauthorized");
  return (await res.json()) as { user: SessionUser };
}

export async function signOut(): Promise<void> {
  await fetch(`${BASE}/auth/logout`, { method: "POST", credentials: "include" });
}

export const api = {
  dashboard: () => request<DashboardResponse>("/api/dashboard"),

  postWeight: (data: WeightInput) =>
    request<{ id: number; recorded_at: string }>("/api/weight", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listWeights: (limit = 30) => request<{ weights: WeightRow[] }>(`/api/weight?limit=${limit}`),
  body: {
    overview: () => request<BodyOverview>("/api/body/overview"),
    series: (days = 90) => request<BodySeries>(`/api/body/series?days=${days}`),
    goal: () => request<BodyGoal>("/api/body/goal"),
  },
  diet: {
    overview: () => request<DietOverview>("/api/diet/overview"),
    savePlan: (data: Partial<DietPlan>) =>
      request<{ ok: true }>("/api/diet/plan", { method: "POST", body: JSON.stringify(data) }),
    savePhaseMeals: (phaseId: number, meals: DietMeal[]) =>
      request<{ ok: true; count: number }>(`/api/diet/phase/${phaseId}/meals`, {
        method: "PUT",
        body: JSON.stringify({ meals }),
      }),
    today: (date?: string) =>
      request<DietToday>(`/api/diet/today${date ? `?date=${date}` : ""}`),
    setDayType: (day_type: string, date?: string) =>
      request<{ ok: true }>("/api/diet/today/day-type", { method: "POST", body: JSON.stringify({ day_type, date }) }),
    logMeal: (meal_index: number, date?: string) =>
      request<{ ok: true }>("/api/diet/today/log-meal", { method: "POST", body: JSON.stringify({ meal_index, date }) }),
    clearMeal: (meal_index: number, date?: string) =>
      request<{ ok: true }>("/api/diet/today/clear-meal", { method: "POST", body: JSON.stringify({ meal_index, date }) }),
    report: () => request<DietReport>("/api/diet/report"),
    suggestMeal: (meal_index: number) =>
      request<DietSuggestResponse>("/api/diet/suggest-meal", {
        method: "POST",
        body: JSON.stringify({ meal_index }),
      }),
    checkin: () => request<DietCheckin>("/api/diet/checkin"),
    applyCheckin: (to_phase_id: number) =>
      request<{ ok: true; active_phase_id: number }>("/api/diet/checkin/apply", {
        method: "POST",
        body: JSON.stringify({ to_phase_id }),
      }),
  },

  postFood: (data: FoodInput) =>
    request<{ id: number; logged_at: string }>("/api/food", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  // Logging without an AI key: saved items, the Open Food Facts database, and
  // free text parsed later by Claude Code on the workstation.
  quickList: () => request<QuickFoodResponse>("/api/food/quick"),
  quickLog: (data: { id: number; from: "favourite" | "log"; multiplier?: number; meal_type?: string }) =>
    request<{ id: number }>("/api/food/quick", { method: "POST", body: JSON.stringify(data) }),
  addFavourite: (data: Record<string, unknown>) =>
    request<{ id: number }>("/api/food/favourites", { method: "POST", body: JSON.stringify(data) }),
  removeFavourite: (id: number) =>
    request<{ ok: true }>(`/api/food/favourites/${id}`, { method: "DELETE" }),
  searchFoods: (q: string) => request<{ items: OffItem[] }>(`/api/food/search?q=${encodeURIComponent(q)}`),
  foodByBarcode: (code: string) => request<{ item: OffItem }>(`/api/food/barcode/${encodeURIComponent(code)}`),
  /** Queues a photo for the workstation parser. Multipart, so it skips `request`. */
  queueFoodPhoto: async (file: Blob, mealType?: string, note?: string) => {
    const fd = new FormData();
    fd.append("photo", file, "meal.jpg");
    if (mealType) fd.append("meal_type", mealType);
    if (note) fd.append("note", note);
    const res = await fetch(`${BASE}/api/food/queue/photo`, { method: "POST", credentials: "include", body: fd });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, body);
    return body as { id: number; status: string };
  },
  /** Sends a spoken note; Whisper reads it on the workstation. */
  queueAudio: async (wav: Blob, seconds: number) => {
    const fd = new FormData();
    fd.append("audio", wav, "note.wav");
    fd.append("seconds", String(seconds));
    const res = await fetch(`${BASE}/api/food/queue/audio`, { method: "POST", credentials: "include", body: fd });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, body);
    return body as { id: number; status: string };
  },
  foodPhotoUrl: (id: number) => `${BASE}/api/food/photo/${id}`,
  foodQueue: () => request<{ queue: FoodQueueRow[] }>("/api/food/queue"),
  queueFood: (text: string, meal_type?: string) =>
    request<{ id: number; status: string }>("/api/food/queue", {
      method: "POST",
      body: JSON.stringify({ text, meal_type }),
    }),
  unqueueFood: (id: number) => request<{ ok: true }>(`/api/food/queue/${id}`, { method: "DELETE" }),
  /** Adds a note to something still waiting to be parsed. */
  noteOnQueued: (id: number, text: string) =>
    request<{ ok: true }>(`/api/food/queue/${id}`, { method: "PATCH", body: JSON.stringify({ text }) }),

  nutrition: {
    summary: (days = 30) => request<NutritionSummary>(`/api/nutrition/summary?days=${days}`),
    setTargets: (data: { calories?: number; protein_g?: number }) =>
      request<{ ok: true }>("/api/nutrition/targets", { method: "PUT", body: JSON.stringify(data) }),
  },

  listFood: (limit = 20) => request<{ food: FoodRow[] }>(`/api/food?limit=${limit}`),

  // Photo upload is multipart — the browser sets the Content-Type boundary, so
  // this bypasses the JSON `request` helper.
  foodPhoto: async (file: Blob, mealType?: string, note?: string) => {
    const fd = new FormData();
    fd.append("photo", file, "meal.jpg");
    if (mealType) fd.append("meal_type", mealType);
    if (note) fd.append("note", note);
    const res = await fetch(`${BASE}/api/food/photo`, {
      method: "POST",
      credentials: "include",
      body: fd,
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, body);
    return body as FoodParseResponse;
  },
  foodVoice: (text: string, mealType?: string) =>
    request<FoodParseResponse>("/api/food/voice", {
      method: "POST",
      body: JSON.stringify({ text, meal_type: mealType }),
    }),
  foodConfirm: (data: FoodConfirmInput) =>
    request<{ ok: true; id: number }>("/api/food/confirm", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  deleteFood: (id: number) =>
    request<{ ok: true }>(`/api/food/${id}`, { method: "DELETE" }),

  postExercise: (data: ExerciseInput) =>
    request<{ id: number; started_at: string }>("/api/exercise", {
      method: "POST",
      body: JSON.stringify(data),
    }),
  listExercise: (limit = 20) =>
    request<{ exercise: ExerciseRow[] }>(`/api/exercise?limit=${limit}`),

  // --- Phase 2 surfaces (backend lands incrementally; pages catch
  //     ApiError → empty state until then) ---
  activity: (days = 14, sessions = 60) =>
    request<ActivityResponse>(`/api/activity?days=${days}&sessions=${sessions}`),
  activitySession: (id: number) => request<ActivitySessionResponse>(`/api/activity/session/${id}`),
  heart: {
    overview: (days = 30) => request<HeartRateResponse>(`/api/heart-rate?days=${days}`),
    zones: (days = 7) => request<HrZonesResponse>(`/api/heart-rate/zones?days=${days}`),
    resting: (days = 90) => request<HrRestingResponse>(`/api/heart-rate/resting?days=${days}`),
    peaks: (days = 7) => request<HrPeaksResponse>(`/api/heart-rate/peaks?days=${days}`),
    hrv: (days = 90) => request<HrHrvResponse>(`/api/heart-rate/hrv?days=${days}`),
    insights: (days = 30) => request<HrInsights>(`/api/heart-rate/insights?days=${days}`),
    reports: () => request<{ reports: HrWeeklyReport[] }>("/api/heart-rate/reports"),
    generateReport: () =>
      request<HrWeeklyReport>("/api/heart-rate/reports", { method: "POST", body: "{}" }),
  },
  sleep: (days = 14) => request<SleepResponse>(`/api/sleep?days=${days}`),
  connections: () => request<ConnectionsResponse>("/api/connections"),

  trends: {
    weight: (days = 90) => request<TrendSeries>(`/api/trends/weight?days=${days}`),
    calories: (days = 30) => request<TrendSeries>(`/api/trends/calories?days=${days}`),
    sleep: (days = 30) => request<TrendSeries>(`/api/trends/sleep?days=${days}`),
    activity: (days = 30) => request<TrendSeries>(`/api/trends/activity?days=${days}`),
    body: (days = 90) => request<TrendSeries>(`/api/trends/body?days=${days}`),
  },

  settings: () => request<SettingsResponse>("/api/settings"),
  postSettings: (data: Partial<SettingsResponse>) =>
    request<{ ok: true }>("/api/settings", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  coachToday: () => request<CoachMessage>("/api/coaching/today"),
  coachGenerate: () =>
    request<CoachMessage>("/api/coaching/generate", { method: "POST", body: "{}" }),

  getGoals: () => request<{ goal: GoalDetail | null }>("/api/goals"),
  postGoals: (data: GoalInput) =>
    request<{ ok: true; id: number }>("/api/goals", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  usage: () => request<UsageResponse>("/api/usage"),
  runBackup: () =>
    request<{ ok: true; key: string; rows: number }>("/api/usage/backup", {
      method: "POST",
      body: "{}",
    }),
};

// ---------- types ----------

export interface DashboardResponse {
  today: string;
  timezone: string;
  latest_weight: WeightRow | null;
  food_today: { calories: number; protein: number; carbs: number; fat: number; entries: number };
  exercise_today: { duration_min: number; calories_burned: number; steps: number; sessions: number };
  recent_food: FoodRow[];
  goal: GoalRow | null;
  /** When the Withings scale last reached Withings' servers. */
  scale_last_sync: string | null;
  health: {
    steps: number | null;
    active_minutes: number | null;
    resting_hr: number | null;
    avg_hr: number | null;
    sleep_duration_min: number | null;
    sleep_score: number | null;
  } | null;
  last_workout: Pick<ExerciseRow, "id" | "started_at" | "activity_type" | "duration_min" | "distance_km" | "avg_hr" | "max_hr" | "avg_speed_mps" | "calories_burned"> | null;
  week: { sessions: number; duration_min: number; distance_km: number; run_km: number; runs: number | null };
  prior_7d: { avg_steps: number | null; avg_resting_hr: number | null; avg_sleep_min: number | null } | null;
}

export interface WeightInput {
  recorded_at?: string;
  weight_kg: number;
  body_fat_pct?: number;
  notes?: string;
  /** Per-submit nonce so a double-tap / retry dedups server-side. */
  idempotency_key?: string;
}

export interface WeightRow {
  id: number;
  recorded_at: string;
  weight_kg: number;
  body_fat_pct: number | null;
  source: string;
}

// ---- Body / scale section ----

export interface BodyWeighIn {
  recorded_at: string;
  weight_kg: number;
  body_fat_pct: number | null;
  muscle_mass_kg: number | null;
  water_pct: number | null;
  bone_mass_kg: number | null;
}

export interface BodyOverview {
  latest: BodyWeighIn | null;
  bmi: number | null;
  bmi_category: string | null;
  fat_mass_kg: number | null;
  lean_mass_kg: number | null;
  avg7: number | null;
  delta_last: number | null;
  delta_7d: number | null;
  delta_30d: number | null;
  weighins?: number;
}

export interface BodyDayPoint {
  date: string;
  weight_kg: number;
  avg7: number;
  body_fat_pct: number | null;
  muscle_mass_kg: number | null;
  water_pct: number | null;
  bone_mass_kg: number | null;
}

export interface BodySeries {
  points: BodyDayPoint[];
  rate_kg_per_week: number | null;
  first: BodyDayPoint | null;
  last: BodyDayPoint | null;
}

export interface BodyGoal {
  goal: {
    set_at: string;
    target_weight_kg: number | null;
    target_date: string | null;
    daily_calorie_target?: number | null;
    daily_protein_target_g?: number | null;
    daily_steps_target?: number | null;
  } | null;
  today: string;
  start_weight: number | null;
  current_weight: number | null;
  target_weight: number | null;
  to_go: number | null;
  pct_complete: number | null;
  rate_kg_per_week: number | null;
  projected_date: string | null;
  status: "no_goal" | "reached" | "ahead" | "behind" | "on_track" | "stalled";
}

/** How a day is judged. "partial" and "none" mean the record is too thin to score. */
export type Verdict = "good" | "over" | "under" | "partial" | "none";

export type DayType = "rest" | "light" | "moderate" | "hard";

export interface NutritionDay {
  date: string;
  calories: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  entries: number;
  steps: number | null;
  exercise_kcal: number | null;
  weight_kg: number | null;
  target: number;
  protein_target: number;
  carbs_target: number | null;
  fat_target: number | null;
  veg_target: number | null;
  day_type: DayType;
  day_type_set: boolean;
  maintenance: number;
  balance: number;
  verdict: Verdict;
}

export interface NutritionSummary {
  today: string;
  timezone: string;
  targets: {
    calories: number;
    protein_g: number;
    carbs_g: number | null;
    fat_g: number | null;
    veggie_cups: number | null;
    maintenance: number;
    /** Where the target came from: the RP phase, a goal you set, or worked out. */
    source: "rp" | "goal" | "estimated";
    phase: string | null;
    day_type: DayType;
    day_type_set: boolean;
    goal_weight_kg: number | null;
    latest_weight_kg: number | null;
  };
  today_row: NutritionDay;
  days: NutritionDay[];
  summary: {
    days_logged: number;
    days_tracked: number;
    counts: Record<Verdict, number>;
    avg_calories: number | null;
    avg_protein: number | null;
    avg_balance: number | null;
    good_streak: number;
    predicted_kg: number | null;
    actual_kg: number | null;
  };
}

export interface FavouriteRow {
  id: number;
  description: string;
  calories_kcal: number;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  serving_note: string | null;
  barcode: string | null;
  uses: number;
}

export interface RecentMealRow {
  id: number;
  description: string;
  times: number;
  last_logged: string;
}

export interface QuickFoodResponse {
  favourites: FavouriteRow[];
  recents: RecentMealRow[];
}

/** A product from Open Food Facts. */
export interface OffItem {
  barcode: string | null;
  name: string;
  brand: string | null;
  serving_note: string | null;
  serving_g: number | null;
  per_100g: OffNutrients;
  per_serving: OffNutrients | null;
}

export interface OffNutrients {
  calories_kcal: number | null;
  protein_g: number | null;
  carbs_g: number | null;
  fat_g: number | null;
  fiber_g: number | null;
  sugar_g: number | null;
  sodium_mg: number | null;
}

export interface FoodQueueRow {
  /** What Whisper heard, for a spoken note. */
  transcript?: string | null;
  /** What the note became, as JSON: counts of food and workouts, a weight, a day type. */
  applied_json?: string | null;
  id: number;
  created_at: string;
  text: string;
  meal_type: string | null;
  kind?: "text" | "photo" | "audio";
  status: "pending" | "done" | "failed";
  error: string | null;
  processed_at: string | null;
}

export interface FoodInput {
  logged_at?: string;
  meal_type?: "breakfast" | "lunch" | "dinner" | "snack";
  description: string;
  calories_kcal: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  /** Per-submit nonce so a double-tap / retry dedups server-side. */
  idempotency_key?: string;
}

export interface FoodRow {
  photo_r2_key?: string | null;
  id: number;
  logged_at: string;
  meal_type: string | null;
  description: string;
  calories_kcal: number;
  protein_g: number | null;
  source: string;
  confirmed: number;
}

export interface ExerciseInput {
  started_at?: string;
  activity_type: string;
  duration_min: number;
  calories_burned?: number;
  steps?: number;
  distance_km?: number;
}

export interface ExerciseRow {
  id: number;
  started_at: string;
  ended_at: string | null;
  activity_type: string;
  duration_min: number;
  calories_burned: number | null;
  avg_hr: number | null;
  distance_km: number | null;
  steps: number | null;
  source: string;
  max_hr?: number | null;
  active_calories?: number | null;
  elevation_gain_m?: number | null;
  avg_speed_mps?: number | null;
  max_speed_mps?: number | null;
}

export interface ActivitySessionResponse {
  session: ExerciseRow;
  heart_rate: { time: string; avg_bpm: number; min_bpm: number | null; max_bpm: number | null }[];
}

export interface GoalRow {
  target_weight_kg: number | null;
  target_date: string | null;
  daily_calorie_target: number | null;
  daily_protein_target_g: number | null;
  daily_steps_target: number | null;
}

// ---------- Phase 2 shapes ----------

export interface ActivityDay {
  date: string;
  steps: number | null;
  active_minutes: number | null;
  calories_burned: number | null;
  resting_hr: number | null;
  avg_hr: number | null;
}

export interface ActivityResponse {
  today: ActivityDay | null;
  days: ActivityDay[];
  timezone?: string;
  sessions: ExerciseRow[];
}

export interface SleepNight {
  date: string;
  start_time: string;
  end_time: string;
  duration_min: number;
  light_min: number | null;
  deep_min: number | null;
  rem_min: number | null;
  awake_min: number | null;
  score: number | null;
  source: string;
  /** Sessions the watch split this night into. */
  pieces?: number;
}

export interface SleepResponse {
  last_night: SleepNight | null;
  nights: SleepNight[];
}

export interface HeartRateResponse {
  today: {
    resting: number | null;
    avg: number | null;
    min: number | null;
    max: number | null;
    readings: number;
  } | null;
  /** Today's 5-minute buckets, chronological. */
  intraday: Array<{ t: string; bpm: number; lo: number | null; hi: number | null }>;
  /** Per-day resting + average, oldest → newest. */
  daily: Array<{ date: string; resting_hr: number | null; avg_hr: number | null }>;
}

export interface HrZone {
  name: string;
  color: string;
  lo_bpm: number;
  hi_bpm: number;
  today_min: number;
  period_min: number;
}
export interface HrZonesResponse {
  age: number;
  max_hr: number;
  period_days: number;
  today_total_min: number;
  period_total_min: number;
  zones: HrZone[];
}

export interface HrRestingResponse {
  series: Array<{ date: string; resting_hr: number }>;
  latest: number | null;
  avg7: number | null;
  avg30: number | null;
  min: number | null;
  max: number | null;
  days_with_data: number;
}

export interface HrPeak {
  start: string;
  end: string;
  peak_bpm: number;
  avg_bpm: number;
  duration_min: number;
}
export interface HrPeaksResponse {
  age: number;
  max_hr: number;
  threshold_bpm: number;
  days: number;
  periods: HrPeak[];
}

export interface HrHrvResponse {
  available: boolean;
  series: Array<{ date: string; hrv_ms: number }>;
}

// ---- Heart insights / weekly report ----

export interface HrInsights {
  age: number;
  sex: string | null;
  max_hr: number;
  fitness: {
    vo2max: number | null;
    resting_used: number | null;
    band: "poor" | "fair" | "good" | "excellent" | null;
    expected: number;
    vs_average: number | null;
    series: Array<{ date: string; vo2max: number | null }>;
  };
  anomalies: {
    baseline_recent: number | null;
    days: Array<{ date: string; resting_hr: number; baseline: number; over_by: number }>;
  };
  hour_pattern: Array<{ hour: number; avg: number | null }>;
  day_pattern: Array<{ dow: number; label: string; avg: number | null }>;
  zones_week: Array<{ name: string; color: string; min: number }>;
  sleeping_hr: number | null;
  recovery_available: boolean;
}

// ---- Diet (RP template) ----

export interface DietMeal {
  id?: number;
  phase_id?: number;
  meal_index: number;
  name: string | null;
  timing: string | null;
  protein_g: number;
  fat_g: number;
  veggie_cups: number;
  carbs_rest_g: number;
  carbs_light_g: number;
  carbs_moderate_g: number;
  carbs_hard_g: number;
  is_shake: number;
}

export interface DietPhase {
  id: number;
  ord: number;
  key: string;
  name: string;
  kind: string;
  meals: DietMeal[];
}

export interface DietPlan {
  id: number;
  goal: string;
  start_weight_kg: number | null;
  active_phase_id: number | null;
  meals_per_day: number;
  daily_protein_g: number | null;
  daily_fat_g: number | null;
  daily_veg_cups: number | null;
  daily_carbs_rest_g: number | null;
  daily_carbs_light_g: number | null;
  daily_carbs_moderate_g: number | null;
  daily_carbs_hard_g: number | null;
}

export interface DietFoodSource {
  id: number;
  category: string;
  name: string;
  serving_note: string | null;
}

export interface DietOverview {
  plan: DietPlan | null;
  phases: DietPhase[];
  food_sources: DietFoodSource[];
}

export interface DietMacros {
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  veggie_cups: number;
  kcal?: number;
}
export interface DietTodayMeal {
  meal_index: number;
  name: string | null;
  timing: string | null;
  is_shake: number;
  target: DietMacros;
  logged: { protein_g: number; carbs_g: number; fat_g: number; veggie_cups: number; count: number };
}
export interface DietToday {
  date: string;
  day_type: "rest" | "light" | "moderate" | "hard";
  phase: { id: number; name: string; kind: string } | null;
  meals: DietTodayMeal[];
  totals: { target: DietMacros; logged: Omit<DietMacros, "kcal"> };
}

export interface DietPhaseLite {
  id: number;
  ord: number;
  key: string;
  name: string;
}
export interface DietMealSuggestion {
  name: string;
  items: string;
  note?: string;
}
export interface DietSuggestResponse {
  target: { protein: number; carbs: number; fat: number; veg: number };
  suggestions: DietMealSuggestion[];
}

export interface DietReport {
  phase: { id: number; name: string } | null;
  week_start: string;
  today: string;
  days_logged: number;
  on_plan_pct: number | null;
  adherence: { protein: number | null; carbs: number | null; fat: number | null };
  avg_logged: { protein_g: number; carbs_g: number; fat_g: number } | null;
  adjustments: Array<{
    week_start: string;
    decision: string | null;
    reason: string | null;
    rate_kg_week: number | null;
    applied: number;
    from_name: string | null;
    to_name: string | null;
  }>;
}

export interface DietCheckin {
  has_plan: boolean;
  has_enough_data: boolean;
  goal: string;
  current_phase: DietPhaseLite | null;
  target_phase: DietPhaseLite | null;
  decision: "keep" | "advance" | "regress" | null;
  reason: string | null;
  current_weight_kg: number | null;
  rate_kg_week: number | null;
  rate_lb_week: number | null;
  band_lb: { lo: number; hi: number } | null;
}

export interface HrWeeklyReport {
  week_start: string;
  generated_at: string;
  summary: {
    days_with_data: number;
    hr_minutes: number;
    active_minutes: number;
    avg_hr: number | null;
    max_hr_seen: number | null;
    avg_resting: number | null;
    min_resting: number | null;
    max_resting: number | null;
    vo2max: number | null;
    fitness_band: string | null;
    anomaly_days: number;
    dominant_zone: string | null;
  };
}

export type ProviderId = "garmin" | "samsung" | "withings";

export interface ProviderStatus {
  id: ProviderId;
  label: string;
  connected: boolean;
  last_sync_at: string | null;
  bridge_version?: string | null;
  /** Full-page nav target to begin OAuth, when applicable. */
  connect_url?: string;
  /** True for the Galaxy bridge (sideloaded app, not a web OAuth). */
  is_bridge?: boolean;
}

export interface ConnectionsResponse {
  providers: ProviderStatus[];
}

export interface TrendPoint {
  date: string;
  value: number | null;
  /** Optional secondary series (e.g. moving average, calories out). */
  value2?: number | null;
}

export interface TrendSeries {
  metric: string;
  unit: string;
  points: TrendPoint[];
}

export interface SettingsResponse {
  display_name: string;
  height_cm: number | null;
  sex: string | null;
  date_of_birth: string | null;
  timezone: string;
  coach_tone: string;
  weight_unit: string;
}

export interface CoachMessage {
  for_date: string;
  content: string | null;
  generated_at: string | null;
  model: string | null;
}

// ---- Food AI (M2) ----

export interface FoodParse {
  description?: string;
  items?: Array<{
    name?: string;
    estimated_grams?: number;
    calories_kcal?: number;
    protein_g?: number;
    carbs_g?: number;
    fat_g?: number;
  }>;
  totals?: {
    calories_kcal?: number;
    protein_g?: number;
    carbs_g?: number;
    fat_g?: number;
    fiber_g?: number;
  };
  confidence?: number;
  assumptions?: string[];
  warnings?: string[];
  veggie_cups?: number;
  meal_slot?: number | null;
  on_plan?: boolean;
  fit?: string;
}

export interface FoodParseResponse {
  id: number;
  logged_at: string;
  parse: FoodParse;
}

export interface FoodConfirmInput {
  id: number;
  description?: string;
  meal_type?: "breakfast" | "lunch" | "dinner" | "snack";
  calories_kcal?: number;
  protein_g?: number;
  carbs_g?: number;
  fat_g?: number;
  fiber_g?: number;
  veggie_cups?: number;
  meal_slot?: number | null;
  on_plan?: boolean;
}

// ---- Goals (M7) ----

export interface GoalDetail {
  id: number;
  set_at: string;
  target_weight_kg: number | null;
  target_date: string | null;
  daily_calorie_target: number | null;
  daily_protein_target_g: number | null;
  daily_steps_target: number | null;
  notes: string | null;
}

export interface GoalInput {
  target_weight_kg?: number | null;
  target_date?: string | null;
  daily_calorie_target?: number | null;
  daily_protein_target_g?: number | null;
  daily_steps_target?: number | null;
  notes?: string | null;
}

// ---- Usage & backup ----

export interface UsagePurposeRow {
  purpose: string;
  calls: number;
  cost: number;
  input_tokens: number;
  output_tokens: number;
}

export interface UsageRecentRow {
  called_at: string;
  purpose: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd_estimate: number;
  success: number;
}

export interface UsageResponse {
  total: { calls: number; cost: number; input_tokens: number; output_tokens: number };
  by_purpose: UsagePurposeRow[];
  recent: UsageRecentRow[];
  backup: { last_at: string | null; last_key: string | null; last_rows: number | null };
}
